import PropTypes from 'prop-types';
import { useIsGlobetrotter, useIsMobile } from '@bwoty-web/ui-kit/hooks';
import { useState, useEffect, lazy, Suspense } from 'react';
import {
  ArticleCard,
  Carousel,
  BounceLoader,
  BaseModal,
  ModalBleed,
  Grid,
  GridItem,
} from '@bwoty-web/ui-kit';

import TileContent from './content/TileContent';
import WeatherChart from '../../common/weather/weatherChart';
import WeatherContent from './content/WeatherContent';
import WeatherOverlay from '../../common/weather/weatherOverlay';
import ExcursionContent from './content/ExcursionsContent';
import { logTiles } from '../../utils/log';
import { getGoogleImageUrl } from '../../common/map/googleImageUrl';
import { updateUrlWithOpenTilePush } from '../../utils/url';
import { itemTypes, isCountry, isResort } from '../../utils/geoTypeUtils';

import './tilesView.scss';

const MapContent = lazy(() =>
  import(/* webpackChunkName: "map-content" */ '../../common/map/MapContent'),
);

const MapContentResort = lazy(() =>
  import(
    /* webpackChunkName: "map-content-resort" */ '../../common/map/MapContentResort'
  ),
);

const globeMobileImageSize = '254x382';
const globeDesktopImageSize = '288x432';

const MapLoader = () => (
  <div
    style={{
      minHeight: 200,
      marginTop: 48,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <BounceLoader />
  </div>
);

const TilesView = ({
  activeTileId,
  baseUrl,
  imageDomain,
  initialIsMobile,
  itemContent,
  itemType,
  siteId,
  tiles,
  wvId,
}) => {
  const isGlobe = useIsGlobetrotter();
  const isMobile = useIsMobile(initialIsMobile);
  const [expandedTile, setExpandedTile] = useState(activeTileId || -1);
  const [mapIsLoaded, setMapIsLoaded] = useState(false);

  useEffect(() => {
    if (activeTileId !== null) {
      const tile = tiles?.find((t) => t?.key === activeTileId);
      setExpandedTile(tile?.key);
      if (tile?.key === 'map') {
        setMapIsLoaded(true);
      }
    }
    logTiles.visibleTiles(tiles, itemType);
  }, [activeTileId, tiles, itemType]);

  const openTilePush = (key) => {
    if (key !== expandedTile) {
      setExpandedTile(key);
      updateUrlWithOpenTilePush(key);
      logTiles.clickOnTile(key, itemType);
      if (key === 'map' && !mapIsLoaded) {
        setMapIsLoaded(true);
      }
    }
  };

  const closeTilePush = (key) => {
    if (key === expandedTile) {
      setExpandedTile(-1);
      updateUrlWithOpenTilePush(null);
    }
  };

  const getPushContent = (tile) => {
    switch (tile?.key) {
      case 'climateAndWeather': {
        const weatherChart = !isCountry(itemType) && (
          <WeatherChart
            climate={tile?.climateData}
            currentMonth={new Date().getMonth()}
            siteId={siteId}
            texts={{
              explanationText: tile?.labels?.climateExplanation,
              rainFreeDaysText: tile?.labels?.rainFreeDays,
              sunHoursPerDayText: tile?.labels?.sunHoursPerDay,
              waterTempText: tile?.labels?.waterTemperature,
            }}
            key={tile?.key}
            tightNavPosition
          />
        );
        return (
          <WeatherContent
            close={(key) => closeTilePush(key)}
            isExpanded={expandedTile === tile?.key}
            itemType={itemType}
            tile={tile}
            weatherChart={weatherChart}
            key={tile?.key}
          />
        );
      }
      case 'passportAndVisa': // for country
      case 'flightAndTransfer':
      case 'foodAndNightlife':
      case 'activitiesAndAttractions':
      case 'howToGetAround':
      case 'beach':
      case 'shopping':
      case 'children':
      case 'destination':
      case 'activeTraining':
      case 'passportVisa': // for resort Globe
      case 'inoculations': {
        return (
          <TileContent
            close={(key) => closeTilePush(key)}
            isExpanded={expandedTile === tile?.key}
            tile={tile}
            key={tile?.key}
          />
        );
      }
      case 'excursions': {
        return (
          <ExcursionContent
            close={(key) => closeTilePush(key)}
            isExpanded={expandedTile === tile?.key}
            siteId={siteId}
            tile={tile}
            wvId={wvId}
            key={tile?.key}
          />
        );
      }

      default:
        return null;
    }
  };

  const getMapModal = () => {
    const tile = tiles.find((t) => t?.key === 'map');
    if (!tile) return null;

    if (!isResort(itemType)) {
      return (
        <Suspense fallback={<MapLoader />}>
          <div style={{ minHeight: 248 }}>
            <ModalBleed>
              <MapContent
                itemContent={{ ...itemContent, itemType }}
                labels={{
                  guestRatingLinkText: tile?.labels?.guestRatingLinkText,
                  hotelLinkText: tile?.labels?.hotelLinkText,
                  isSuitableForTrainingText:
                    tile?.labels?.isSuitableForTrainingText,
                  noGuestRatingText: tile?.labels?.noGuestRatingText,
                  numberOfResortsLinkText:
                    tile?.labels?.numberOfResortsLinkText,
                  readMoreButtonText: tile?.labels?.readMoreButtonText,
                }}
                resorts={tile?.content?.resorts || []}
              />
            </ModalBleed>
          </div>
        </Suspense>
      );
    }

    return (
      <Suspense fallback={<MapLoader />}>
        <div style={{ minHeight: 248 }}>
          <ModalBleed>
            <MapContentResort
              baseUrl={baseUrl}
              imageDomain={imageDomain}
              isExpanded
              resortId={wvId}
              siteId={siteId}
              texts={{
                adultFriendly: tile?.labels?.adultFriendly,
                button: tile?.labels?.hotelCardSelectBtn,
                childFriendly: tile?.labels?.childFriendly,
                guestRating: tile?.labels?.guestRating,
                noPriceLabel: tile?.labels?.noPriceLabel,
                tripAdvisor: tile?.labels?.tripAdvisor,
              }}
            />
          </ModalBleed>
        </div>
      </Suspense>
    );
  };

  const getWeatherOverlay = (tile, weatherItemType) => (
    <WeatherOverlay
      climateInLabel={tile.labels?.climateIn}
      itemName={tile?.itemName}
      itemType={weatherItemType}
      siteId={siteId}
      temperature={tile.climateData?.temperature}
    />
  );

  let imageSize = globeMobileImageSize;
  if (!isMobile && isGlobe) imageSize = globeDesktopImageSize;
  else if (isMobile && !isGlobe) imageSize = '330x220';
  else if (!isMobile && !isGlobe) imageSize = '384x250';

  const googleImage = (
    <img
      className="gw-tiles-view__map-image"
      alt="google map"
      src={getGoogleImageUrl(itemContent, imageSize, isGlobe)}
    />
  );

  if (!tiles || tiles?.length <= 0) return null;

  const content = tiles?.map((tile) => {
    const isMapTile = tile?.key === 'map';
    const isWeatherTile = tile?.key === 'climateAndWeather';
    const WrapperElement = isGlobe ? 'div' : GridItem;

    return (
      <WrapperElement
        key={`tile-item-${tile?.key}`}
        {...(!isGlobe && { columnSpan: 4 })}
      >
        <ArticleCard
          className="gw-tiles-view__card"
          columnSpan={isGlobe ? 3 : 4}
          externalImage={isMapTile && isGlobe ? googleImage : null}
          id={`${tile?.key}Push`}
          image={{
            cloudinaryUriTemplate:
              tile?.backgroundImage?.media?.cloudinaryUriTemplate,
          }}
          imageAspectRatio={isGlobe ? 2 / 3 : 3 / 2}
          key={tile?.key}
          onClick={() => openTilePush(tile?.key)}
          onClickElementProps={{ 'aria-haspopup': 'dialog' }}
          title={tile?.header}
          {...(isWeatherTile &&
            !isGlobe && {
              imageOverlay: getWeatherOverlay(tile, itemType),
            })}
        />
      </WrapperElement>
    );
  });

  return (
    <div className="gw-tiles-view">
      <BaseModal
        isOpen={expandedTile === 'map'}
        onClose={() => closeTilePush('map')}
        overlaidCloseButton
        size="l"
      >
        {mapIsLoaded && getMapModal()}
      </BaseModal>
      {tiles.map((tile) => getPushContent(tile))}
      {isGlobe ? (
        <Carousel itemSize={3}>{content}</Carousel>
      ) : (
        <Grid>{content}</Grid>
      )}
    </div>
  );
};

TilesView.propTypes = {
  activeTileId: PropTypes.string,
  baseUrl: PropTypes.string.isRequired,
  imageDomain: PropTypes.string.isRequired,
  initialIsMobile: PropTypes.bool.isRequired,
  itemContent: PropTypes.shape({
    extent: PropTypes.shape({
      northEast: PropTypes.shape(),
      southWest: PropTypes.shape(),
    }),
    name: PropTypes.string,
    position: PropTypes.shape({
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }),
    wvId: PropTypes.string.isRequired,
  }).isRequired,
  itemType: PropTypes.string,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  tiles: PropTypes.arrayOf(
    PropTypes.shape({
      backgroundImage: PropTypes.shape({
        media: PropTypes.shape({ cloudinaryUriTemplate: PropTypes.string }),
      }),
      climateData: PropTypes.shape({ temperature: PropTypes.shape({}) }),
      content: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.shape({})),
        PropTypes.shape({}),
      ]),
      header: PropTypes.string.isRequired,
      itemName: PropTypes.string,
      key: PropTypes.string.isRequired,
      labels: PropTypes.shape({
        adultFriendly: PropTypes.string,
        childFriendly: PropTypes.string,
        climateExplanation: PropTypes.string,
        climateIn: PropTypes.string,
        guestRating: PropTypes.string,
        guestRatingLinkText: PropTypes.string,
        hotelCardSelectBtn: PropTypes.string,
        hotelLinkText: PropTypes.string,
        isSuitableForTrainingText: PropTypes.string,
        noGuestRatingText: PropTypes.string,
        noPriceLabel: PropTypes.string,
        numberOfResortsLinkText: PropTypes.string,
        rainFreeDays: PropTypes.string,
        readMoreButtonText: PropTypes.string,
        sunHoursPerDay: PropTypes.string,
        tripAdvisor: PropTypes.string,
        waterTemperature: PropTypes.string,
      }),
    }),
  ).isRequired,
  wvId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

TilesView.defaultProps = {
  activeTileId: null,
  itemType: itemTypes.resort,
};

export default TilesView;
